import { Button, Grid } from "@mui/material";

import { fetchSiteData, reboot, uploadConfig, uploadDevices } from "../../scripts/api_v2/commands_client";
import * as React from "react";
import useBackend, { useAllowedFeatures } from "../../hooks/use_backend";
import { isComponentDisabledForUpdates } from "../../pages/login/login_utils";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import Snackbar from "../../scripts/snackbar";
import { CollapsableCard } from "../general/collapsable_card";
import { useModal } from "../../context/modal_provider";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { RevSSHCmdDialog, ShellCmdDialog } from "./shell_cmd_dialog";
import Privileges from "../../enums/privilege_enum";

export function CommandCenter({ tooltip, permissionMap, required_privilege, ...props }) {
  const backend = useBackend();
  const params = useParams();
  const [shellOpen, setShellOpen] = React.useState(false);
  const [revSSHOpen, setRevSSHOpen] = React.useState(false);
  const { openOverrideModal } = useModal();
  const allowedFeatures = useAllowedFeatures();

  const {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const { mutate: uploadConfigMutation, isPending: configIsMutating } = useMutation({
    mutationFn: async () => {
      return await uploadConfig(backend.fetch, params.site_id);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not upload config: " + err);
    },
  });

  const { mutate: uploadDevicesMutation, isPending: devicesAreMutating } = useMutation({
    mutationFn: async () => {
      return await uploadDevices(backend.fetch, params.site_id);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not upload devices: " + err);
    },
  });

  const { mutate: rebootMutation, isPending: isRebooting } = useMutation({
    mutationFn: async () => {
      return await reboot(backend.fetch, params.site_id);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not reboot: " + err);
    },
    onSuccess: (err, variables, context) => {
      Snackbar.success("Reboot sent");
    },
  });

  const isDisabled = isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());

  function getButton(isDisabled = false, isHidden = false, mutation) {
    return <Button onClick={() => rebootMutation()}>{"Restart software"}</Button>;
  }

  const isHidden = !allowedFeatures.includes(Privileges.ADMIN_OVERVIEW.feature);
  const buttonSX = { borderRadius: 2, mr: 2, my: 1, px: 2, py: 1 };
  const adminButtonSX = { borderRadius: 2, mr: 2, my: 1, px: 2, py: 1, display: isHidden ? "none" : "inline-flex" };

  return (
    <>
      <CollapsableCard
        tooltip={tooltip}
        startCollapsed={false}
        title={`Commands`}
        width={"100%"}
        elevation={0}
        sx={props.sx}
      >
        <Grid container direction="row" spacing={6}>
          <Grid item xs={12} md={4} display="flex" flexDirection="column">
            <Button
              variant="contained"
              sx={buttonSX}
              color="inherit"
              size="small"
              disabled={isDisabled}
              onClick={() => rebootMutation()}
            >
              Restart software
            </Button>
            <Button
              variant="contained"
              sx={adminButtonSX}
              color="inherit"
              size="small"
              disabled={isDisabled}
              onClick={() => uploadConfigMutation()}
            >
              Upload config
            </Button>
          </Grid>
          <Grid item xs={12} md={4} display="flex" flexDirection="column">
            <Button
              variant="contained"
              sx={adminButtonSX}
              color="inherit"
              size="small"
              disabled={isDisabled}
              onClick={() => setRevSSHOpen(true)}
            >
              Reverse ssh
            </Button>
            <Button
              variant="contained"
              sx={adminButtonSX}
              color="inherit"
              size="small"
              disabled={isDisabled}
              onClick={() => uploadDevicesMutation()}
            >
              Upload devices
            </Button>
          </Grid>
          <Grid item xs={12} md={4} display="flex" flexDirection="column">
            <Button
              variant="contained"
              sx={adminButtonSX}
              color="inherit"
              size="small"
              disabled={isDisabled}
              onClick={() => setShellOpen(true)}
            >
              shell cmd
            </Button>
            <Button
              variant="contained"
              sx={adminButtonSX}
              color="inherit"
              size="small"
              disabled={isDisabled}
              onClick={() => openOverrideModal({ siteData })}
            >
              Manual override
            </Button>
          </Grid>
        </Grid>
      </CollapsableCard>
      <ShellCmdDialog open={shellOpen} setOpen={setShellOpen} siteId={params.site_id} />
      <RevSSHCmdDialog open={revSSHOpen} setOpen={setRevSSHOpen} siteId={params.site_id} />
    </>
  );
}
